import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CohnectLogo } from "./CohnectLogo";
import { BRAND } from "@/constants/brand";
import { usePublicTheme } from "@/contexts/ThemeContext";

// Inline Discord icon (kept small; no external deps).
const DiscordIcon = ({ className = "" }) => (
  <svg viewBox="0 -28.5 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M216.856 16.597A208.502 208.502 0 0 0 164.042 0c-2.275 4.113-4.933 9.645-6.766 14.046-19.692-2.961-39.203-2.961-58.533 0-1.832-4.4-4.55-9.933-6.846-14.046A207.809 207.809 0 0 0 39.045 16.597C5.618 66.4-3.443 114.968 1.087 162.848c22.169 16.555 43.653 26.612 64.775 33.193A161.094 161.094 0 0 0 79.735 175.3a136.413 136.413 0 0 1-21.846-10.632 108.636 108.636 0 0 0 5.356-4.237c42.122 19.702 87.89 19.702 129.51 0a131.66 131.66 0 0 0 5.355 4.237 136.07 136.07 0 0 1-21.887 10.653c4.006 8.02 8.638 15.67 13.873 22.735 21.142-6.58 42.646-16.637 64.815-33.213 5.316-55.517-9.08-103.638-38.056-146.246ZM85.474 135.095c-12.645 0-23.015-11.805-23.015-26.18s10.149-26.2 23.015-26.2c12.867 0 23.236 11.804 23.015 26.2.02 14.375-10.148 26.18-23.015 26.18Zm85.051 0c-12.645 0-23.014-11.805-23.014-26.18s10.148-26.2 23.014-26.2c12.867 0 23.236 11.804 23.015 26.2 0 14.375-10.148 26.18-23.015 26.18Z"/>
  </svg>
);

export const DISCORD_INVITE_URL = BRAND.discordInviteUrl;
export { DiscordIcon };

export const DiscordCTA = () => (
  <section data-testid="discord-cta" className="border-t-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper)]">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="nb-box px-6 sm:px-10 py-10 sm:py-12">
        <div className="grid md:grid-cols-[auto,1fr,auto] gap-6 md:gap-10 items-center">
          <div className="inline-flex h-16 w-16 shrink-0 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] text-[color:var(--coh-on-accent)]">
            <DiscordIcon className="h-8 w-8" />
          </div>
          <div className="min-w-0">
            <div className="kicker">Community</div>
            <h3 data-testid="discord-cta-headline" className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight text-[color:var(--coh-ink)]">
              Meet other humans on Discord.
            </h3>
            <p className="mt-4 text-base sm:text-lg leading-relaxed max-w-2xl text-[color:var(--coh-ink-muted)]">
              Join the Cohnect Discord to swap intros, share what you are working on, and connect with
              founders, creators, and quietly thoughtful humans. Warm, low-noise, and always open.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" data-testid="discord-join-button" className="btn-nb btn-nb-primary h-12 px-6">
              <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">
                <DiscordIcon className="h-5 w-5 mr-2" />
                Join the Discord <span aria-hidden="true" className="font-mono ml-1">↗</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const SiteFooter = () => {
  const { theme } = usePublicTheme();
  const logoTone = theme === "dark" ? "light" : "ink";
  return (
    <>
      <DiscordCTA />
      <footer data-testid="site-footer" className="border-t-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <CohnectLogo tone={logoTone} height={24} />
            <p className="mt-4 text-sm text-[color:var(--coh-ink-muted)] max-w-sm">
              Understand, organize, and strengthen your connections — an app and a free weekly letter.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--coh-ink)] underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)] transition-colors" data-testid="footer-discord-link">
                <DiscordIcon className="h-4 w-4" /> Discord
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="kicker">Read</div>
              <ul className="mt-3 space-y-2 text-[color:var(--coh-ink-muted)]">
                <li><Link to="/issues" className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Issues</Link></li>
                <li><Link to="/about" className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">About</Link></li>
                <li><Link to="/support" data-testid="footer-support-link" className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Support</Link></li>
              </ul>
            </div>
            <div>
              <div className="kicker">Info</div>
              <ul className="mt-3 space-y-2 text-[color:var(--coh-ink-muted)]">
                <li><Link to="/contact" className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Contact</Link></li>
                <li><Link to="/privacy" className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Privacy</Link></li>
                <li><Link to="/terms" className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="text-xs text-[color:var(--coh-ink-muted)] leading-relaxed">
            <p className="font-semibold text-[color:var(--coh-ink)]" data-testid="footer-editorial-disclaimer">
              Cohnect is an editorial publication, not medical or mental-health advice.
            </p>
            <p className="mt-3">
              {BRAND.entity} ·{" "}
              <a href={BRAND.emailHref} className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">{BRAND.email}</a>{" "}
              · <a href={BRAND.phoneHref} className="underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">{BRAND.phone}</a>
            </p>
            <p className="mt-3">© {new Date().getFullYear()} {BRAND.entity}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};
